import { useState, useMemo } from 'react';

/**
 * Geographic hierarchy for filtering
 */
export const LOCATION_HIERARCHY = {
  "Maharashtra": {
    "Mumbai": ["Bandra", "Andheri", "Powai", "Chembur", "Colaba", "Marine Drive", "Sion"],
    "Pune": ["Kothrud", "Hinjewadi", "Baner", "Viman Nagar"],
    "Thane": ["Naupada", "Ghodbunder", "Kalyan", "Dombivli"]
  },
  "Gujarat": {
    "Rajkot": ["Raiya Chowk", "Kalavad Road", "Yagnik Road"],
    "Ahmedabad": ["Satellite", "Vastrapur", "Prahlad Nagar"],
    "Surat": ["Adajan", "Vesu", "Varachha"]
  },
  "Delhi": {
    "New Delhi": ["Connaught Place", "Karol Bagh", "Saket", "Hauz Khas", "Vasant Kunj"]
  }
};

/**
 * Hook to manage filtering logic for the Live Monitoring page
 * Handles combined data from incidents and vehicles
 */
const INCIDENT_TYPE_ALIASES = {
  ACCIDENT: "ACCIDENT",
  COLLISION: "ACCIDENT",
  FIRE: "FIRE",
  FIRE_DETECTED: "FIRE",
  PANIC: "PANIC",
  PANIC_BUTTON: "PANIC",
  ROLLOVER: "ROLLOVER",
  HARSH_BRAKE: "HARSH_BRAKE",
  SPEEDING_ALERT: "SPEEDING_ALERT",
  SPEEDING: "SPEEDING_ALERT",
};

export const INCIDENT_TYPE_FILTER_OPTIONS = [
  { value: "all", label: "All Types" },
  { value: "ACCIDENT", label: "Accident" },
  { value: "FIRE", label: "Fire" },
  { value: "PANIC", label: "Panic" },
  { value: "ROLLOVER", label: "Rollover" },
  { value: "HARSH_BRAKE", label: "Harsh Brake" },
  { value: "SPEEDING_ALERT", label: "Speeding Alert" },
];

function normalizeIncidentType(value) {
  const normalized = String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  if (!normalized) return "UNKNOWN";
  return INCIDENT_TYPE_ALIASES[normalized] || normalized;
}

export function useLiveMonitoringFilters(incidents = [], vehicles = []) {
  const [filters, setFilters] = useState({
    location: {
      state: "all",
      district: "all",
      city: "all"
    },
    vehicleCategory: "all",
    status: "all",
    alertType: "all",
    timeRange: "all"
  });

  const resetFilters = () => {
    setFilters({
      location: { state: "all", district: "all", city: "all" },
      vehicleCategory: "all",
      status: "all",
      alertType: "all",
      timeRange: "all"
    });
  };

  const activeData = useMemo(() => {
    const combined = [
      ...incidents.map(i => ({ ...i, dataType: 'incident' })),
      ...vehicles.map(v => ({ ...v, dataType: 'vehicle' }))
    ];

    return combined.filter(item => {
      const itemState = String(item.state || "").toLowerCase();
      const itemDistrict = String(item.district || "").toLowerCase();
      const itemCity = String(item.city || "").toLowerCase();
      const locationString = String(item.location || "").toLowerCase();

      // 1. Location Filter Logic
      if (filters.location.state !== "all") {
        const stateMatch = itemState.includes(filters.location.state.toLowerCase()) || locationString.includes(filters.location.state.toLowerCase());
        if (!stateMatch) return false;
        
        // If city is selected, check if it exists in the location string
        if (filters.location.city !== "all") {
          const cityNeedle = filters.location.city.toLowerCase();
          const cityMatch = itemCity.includes(cityNeedle) || locationString.includes(cityNeedle);
          if (!cityMatch) return false;
        } 
        // If district (City in our hierarchy) is selected, check if it or any of its sub-areas match
        else if (filters.location.district !== "all") {
          const district = filters.location.district;
          const citiesInDistrict = LOCATION_HIERARCHY[filters.location.state][district] || [];
          const matchesDistrict = itemDistrict.includes(district.toLowerCase()) || locationString.includes(district.toLowerCase());
          const matchesAnyCity = citiesInDistrict.some(c => itemCity.includes(c.toLowerCase()) || locationString.includes(c.toLowerCase()));
          if (!matchesDistrict && !matchesAnyCity) return false;
        }
      }

      if (filters.vehicleCategory !== "all") {
        if (item.dataType !== 'vehicle') return false;
        const category = String(item.vehicle_category || item.type || "").toLowerCase();
        if (category !== filters.vehicleCategory.toLowerCase()) return false;
      }

      // 2. Status Filter (primarily for vehicles, but can apply to incidents if needed)
      if (filters.status !== "all") {
        if (item.dataType === 'vehicle' && item.status !== filters.status) return false;
        // For incidents, we might want to map 'status' to incident status if applicable
      }

      // 3. Alert Type Filter (for incidents)
      if (filters.alertType !== "all") {
        if (item.dataType === "vehicle") {
          // Hide vehicles when filtering by incident type.
          return false;
        }

        const incidentType = normalizeIncidentType(
          item.incident_type || item.type || item.alert_type || item.event,
        );
        if (incidentType !== filters.alertType) return false;
      }

      // 4. Time Range Filter (Mock logic)
      if (filters.timeRange !== "all") {
        // In a real app, parse item.timestamp
        // For this demo, we'll keep it simple
      }

      return true;
    });
  }, [filters, incidents, vehicles]);

  return {
    filters,
    setFilters,
    resetFilters,
    activeData
  };
}
